'use client';

import { CmsWorkspaceLayout } from '@/cms/components/blocks/shared/cms-workspace-layout';
import { cn } from '@/lib/ui/utils';
import {
  type ReactNode,
  type RefObject,
  createContext,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import type { WorkspaceStudioRenderModel } from '../contracts/server-model';
import { WorkspaceHeroCopy } from '../shared/workspace-hero-copy';
import { WorkspacePreviewMasonry } from '../tool-views/workspace-preview-masonry';
import {
  type WorkspaceLoadingSurface,
  WorkspaceParameterPanelSkeleton,
  WorkspaceSurfaceSkeleton,
} from './workspace-studio-skeleton';

type SurfaceState = {
  mode: WorkspaceLoadingSurface | 'discovery';
  shouldShowMobileSurface: boolean;
};
const WorkspaceFrameContext = createContext<{
  panel: HTMLDivElement | null;
  surface: HTMLDivElement | null;
  scrollRoot: HTMLDivElement | null;
  history: HTMLDivElement | null;
  setSurface: (state: SurfaceState | null) => void;
} | null>(null);

export function useWorkspaceFrame() {
  return useContext(WorkspaceFrameContext);
}

export function WorkspacePersistentFrame({
  hero,
  initialLoadingSurface = 'canvas',
  children,
}: {
  hero: WorkspaceStudioRenderModel['shell']['hero'];
  initialLoadingSurface?: WorkspaceLoadingSurface;
  children: ReactNode;
}) {
  const [panel, setPanel] = useState<HTMLDivElement | null>(null);
  const [surface, setSurfaceContainer] = useState<HTMLDivElement | null>(null);
  const [scrollRoot, setScrollRoot] = useState<HTMLDivElement | null>(null);
  const [history, setHistory] = useState<HTMLDivElement | null>(null);
  const [surfaceState, setSurface] = useState<SurfaceState | null>(null);
  const hasWorkspaceSurface =
    surfaceState === null || surfaceState.mode !== 'discovery';
  const context = useMemo(
    () => ({ panel, surface, scrollRoot, history, setSurface }),
    [panel, surface, scrollRoot, history]
  );
  return (
    <WorkspaceFrameContext.Provider value={context}>
      <section
        id="workspace"
        data-cms-workspace-root
        className={cn(
          'cms-block-workspace-workspace-1-workspace-shell__section',
          surfaceState?.shouldShowMobileSurface &&
            'cms-block-workspace-workspace-1-workspace-shell__sectionMobileCanvasActive'
        )}
      >
        <CmsWorkspaceLayout
          panelClassName="cms-block-workspace-workspace-1-workspace-shell__panel"
          contentClassName="cms-block-workspace-workspace-1-workspace-shell__workspace"
          panel={
            <>
              {surface === null ? <WorkspaceParameterPanelSkeleton /> : null}
              <div ref={setPanel} style={{ display: 'contents' }} />
            </>
          }
          content={
            <>
              <div
                ref={setScrollRoot}
                data-workspace-canvas-scroll-root=""
                className={cn(
                  'cms-block-workspace-workspace-1-workspace-shell__workspaceCanvasArea',
                  hasWorkspaceSurface &&
                    'cms-block-workspace-workspace-1-workspace-shell__workspaceCanvasAreaVisible',
                  surfaceState?.shouldShowMobileSurface &&
                    'cms-block-workspace-workspace-1-workspace-shell__workspaceCanvasAreaMobileVisible'
                )}
              >
                <div
                  className={cn(
                    'cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceInner',
                    hasWorkspaceSurface
                      ? 'cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceInnerCanvasMode'
                      : 'cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceInnerDiscovery'
                  )}
                >
                  <div
                    className="cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceDiscovery"
                    hidden={hasWorkspaceSurface}
                    style={
                      hasWorkspaceSurface ? { display: 'none' } : undefined
                    }
                  >
                    <div className="cms-block-workspace-workspace-1-workspace-showcase-layout__workspaceDiscoveryHero">
                      <WorkspaceHeroCopy hero={hero} />
                    </div>
                    <WorkspacePreviewMasonry images={hero.previewImages} />
                  </div>
                  {surfaceState === null ? (
                    <WorkspaceSurfaceSkeleton surface={initialLoadingSurface} />
                  ) : null}
                  <div
                    ref={setSurfaceContainer}
                    style={{ display: 'contents' }}
                  />
                </div>
              </div>
              <div ref={setHistory} style={{ display: 'contents' }} />
            </>
          }
        />
      </section>
      {children}
    </WorkspaceFrameContext.Provider>
  );
}

export function WorkspaceFrameSlots({
  panel,
  surface,
  history,
  canvasRef,
  surfaceMode,
  shouldShowMobileSurface,
}: {
  panel: ReactNode;
  surface: ReactNode;
  history: ReactNode;
  canvasRef: RefObject<HTMLDivElement | null>;
  surfaceMode: WorkspaceLoadingSurface | 'discovery';
  shouldShowMobileSurface: boolean;
}) {
  const frame = useWorkspaceFrame();
  const setSurface = frame?.setSurface;
  const canvasContainer = frame?.scrollRoot;
  useLayoutEffect(() => {
    canvasRef.current = canvasContainer ?? null;
    return () => {
      canvasRef.current = null;
    };
  }, [canvasContainer, canvasRef]);
  useLayoutEffect(() => {
    setSurface?.({ mode: surfaceMode, shouldShowMobileSurface });
    return () => setSurface?.(null);
  }, [setSurface, shouldShowMobileSurface, surfaceMode]);
  if (!frame)
    throw new Error('Workspace frame slots require a persistent frame.');
  return (
    <>
      {frame.panel && createPortal(panel, frame.panel)}
      {frame.surface && createPortal(surface, frame.surface)}
      {frame.history && createPortal(history, frame.history)}
    </>
  );
}
